import {useQuery} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import pluralize from "pluralize";
import {Skeleton} from "@/components/ui/skeleton.tsx";
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion.tsx";
import {Card, CardDescription, CardHeader} from "@/components/ui/card.tsx";
import SuggestedMentorCard from "@/components/SuggestedMentorCard.tsx";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getMenteesWithSuggestedMentors} from "@/services/menteeService.ts";

const MenteesSuggestions = () => {
    const {t} = useTranslation();

    const {data: menteesWithSuggestedMentors, isFetching} = useQuery({
        queryKey: queryKeys.fetchSuggestions.mentees,
        queryFn: () => getMenteesWithSuggestedMentors(),
        initialData: [],
    });

    if (isFetching) {
        return (
            <div className="flex flex-col space-y-3">
                <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Skeleton key={i} className="h-12 w-full rounded-md" />
                    ))}
                </div>
            </div>
        );
    }

    const mentees = menteesWithSuggestedMentors ?? [];
    let hasSuggestedMentors = false;
    const suggestedMentors = mentees.map(mentee => {
        if (!hasSuggestedMentors && mentee.menteesSuggestedMentors && mentee.menteesSuggestedMentors.length > 0) {
            hasSuggestedMentors = true;
        }
        const displayName = mentee.user?.fullName ?? ([mentee.user?.firstName, mentee.user?.lastName].filter(Boolean).join(" ").trim() || "");

        return (
            <AccordionItem key={mentee.id} value={mentee.id.toString()}>
                <AccordionTrigger className="flex justify-between items-center">
                    <div className="font-medium text-text-1">
                        {displayName}
                    </div>
                    <div className="ml-auto mr-2 text-sm text-text-2">
                        {
                            mentee.menteesSuggestedMentors.length === 0
                                ? mentee.menteePreference
                                    ? <span className="text-tangerine">{t("suggestions.noMentorsSuggested")}</span>
                                    : <span>{t("suggestions.preferenceNotSubmitted")}</span>
                                : <span>{pluralize("mentor", mentee.menteesSuggestedMentors?.length, true)} suggested</span>
                        }
                    </div>
                </AccordionTrigger>
                <AccordionContent>
                    <a
                        href={`/mentees/${mentee.id}`}
                        className="inline-flex items-center justify-center rounded-md border border-border bg-bg-card px-4 h-10 text-sm font-medium text-text-1 hover:bg-bg-elevated transition-colors focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-card"
                    >
                        {t("suggestions.goToMenteeProfile")}
                    </a>
                    {mentee.menteesSuggestedMentors && mentee.menteesSuggestedMentors.length > 0 && (() => {
                        const affinitySuggestions = mentee.menteesSuggestedMentors.filter(
                            (s) => s.affinityGroupSuggestion === true
                        );
                        const otherSuggestions = mentee.menteesSuggestedMentors.filter(
                            (s) => s.affinityGroupSuggestion !== true
                        );
                        return (
                            <>
                                {affinitySuggestions.length > 0 && (
                                    <>
                                        <h2 className="pt-6 text-lg font-semibold text-text-1">{t("affinity.groupSuggestionsTitle")}</h2>
                                        <SuggestedMentorCard
                                            menteeWithSuggestedMentors={mentee}
                                            suggestions={affinitySuggestions}
                                            showAvailabilities={false}
                                            startIndex={0}
                                        />
                                    </>
                                )}
                                {otherSuggestions.length > 0 && (
                                    <>
                                        <h2 className="pt-6 text-lg font-semibold text-text-1">
                                            {affinitySuggestions.length > 0 ? t("affinity.otherSectionTitle") : t("suggestions.suggestedMentors")}
                                        </h2>
                                        <SuggestedMentorCard
                                            menteeWithSuggestedMentors={mentee}
                                            suggestions={otherSuggestions}
                                            showAvailabilities={false}
                                            startIndex={affinitySuggestions.length}
                                        />
                                    </>
                                )}
                            </>
                        );
                    })()}
                </AccordionContent>
            </AccordionItem>
        );
    });

    if (!hasSuggestedMentors) {
        return (
            <Card className="bg-bg-card border border-border rounded-xl">
                <CardHeader>
                    <CardDescription className="text-base text-text-2">
                        {t("suggestions.notAvailableYet")}
                    </CardDescription>
                </CardHeader>
            </Card>
        );
    }

    return (
        <Accordion type="single" collapsible className="pt-0">
            {suggestedMentors}
        </Accordion>
    );
}

export default MenteesSuggestions;
